import * as THREE from 'three';
import { MOVE, PLAYER, PEEK, TERRAIN, WATER, SURVIVAL, TORCH_FX } from '../config';
import { terrainHeight } from '../engine/terrain';
import { makeGlowTexture } from './nodes';
import type { InputState } from '../engine/input';
import { isWeapon, type WeaponId } from './weapons';
import { buildWeapon3p, buildToolGlb, buildTorchGlb, torchState, applyHeldFit } from '../engine/weaponModels';
import { preloadGLB, cloneGLBIfReady, fitGLBHeld } from '../engine/gltf';
import { weaponFit, type V3 } from '../engine/weaponFit';

const ZERO3: V3 = [0, 0, 0];

// The local player. Movement is camera-relative with momentum (spec section 4). Collision is
// resolved by a callback against the terrain and buildables, so the player walks and jumps on
// foundations. Crouch works mid-air (crouch-jump): holding crouch and jumping reaches higher.
// The body is a simple humanoid; the right arm swings when gathering.

export type CollideFn = (pos: THREE.Vector3, vel: THREE.Vector3, radius: number, height: number) => boolean;
// Returns the tallest the player may stand at (x,z) before their head hits a box above (capped at maxRise).
export type HeadroomFn = (x: number, z: number, feetY: number, radius: number, maxRise: number) => number;

export type HeldItemId = 'hammer' | 'blueprint' | 'rock' | 'ak' | 'chest' | 'bigchest' | 'torch' | 'guidebook'
  | 'wood_axe' | 'wood_pickaxe' | 'stone_axe' | 'stone_pickaxe' | 'metal_axe' | 'metal_pickaxe' | WeaponId | null;

const SWING_DURATION = 0.5;
const ADS_MOVE_SLOW = 0.45; // movement speed multiplier while aiming down sights
const AIM_ARM = 1.45;       // 3rd-person shouldered-aim arm pitch (both arms raise the gun forward)
// 3P swing arc (friend 2026-07-13: sword/mace/tools looked like a "backwards elbow / self-beheading").
// The 3P arm is ONE rigid segment (no elbow), so the old 2.8 rad windup swung it up-and-BEHIND the head.
// Keep the arc in FRONT: raise to a forward-up cock (SWING_RAISE), slam down through the front (SWING_END).
const SWING_RAISE = 2.0; // ~forward-up cock (was 2.8 = overhead/behind)
const SWING_END = 0.35;  // down-and-forward follow-through

export class Player {
  readonly object = new THREE.Group();
  readonly position = new THREE.Vector3(0, 0, 0);
  readonly velocity = new THREE.Vector3(0, 0, 0);

  grounded = true;
  crouching = false;
  sprinting = false;
  aiming = false; // aiming down sights (set by main each frame): slows movement + blocks sprint
  combatAim = false; // holding a gun/bow: the 3rd-person body raises it into a shouldered aim pose
  swimming = false; // submerged in deep water: buoyant float, jump = up, crouch = dive
  fly = false; // dev fly mode (no gravity/collision; jump = up, crouch = down)
  stamina: number = MOVE.staminaMax;
  health: number = PLAYER.maxHealth; // survival HP (combat + sleep-kill build on this)
  readonly spawnPoint = new THREE.Vector3(); // fallback respawn point (set at spawn)
  state: 'alive' | 'downed' | 'dead' = 'alive';
  downedTimer = 0;   // seconds left bleeding out while downed
  reviveChance = 0;  // odds of standing back up when the bleed-out timer ends
  deathT = 0;        // collapse-animation progress (seconds) once dead
  godMode = false;   // dev no-death: when on, ALL damage is ignored (only togglable while dev mode is active)
  hunger: number = SURVIVAL.maxHunger; // 0..max; drains over time, starves HP at 0
  thirst: number = SURVIVAL.maxThirst; // 0..max; drains faster, dehydrates HP at 0
  pendingHeal = 0;   // HP buffered by eating; bled into health slowly (the gain marker on the bar)
  poisonTimer = 0;   // seconds of raw-food poison remaining (HP drain)
  bleeding = false;  // bleeding until bandaged (HP drain)
  onHurt: (() => void) | null = null; // fired on any landed damage (main plays the hurt grunt there; never fires in god mode / while dead)
  private airborneApexY = 0; // highest Y reached during the current airborne arc (for fall damage by real drop)
  // Last hit taken this frame (damage feedback: red flash + shake + directional indicator). main.ts reads
  // + clears this once per frame after update(); world-space source is null for non-directional damage
  // (fall damage, starvation/dehydration, drowning) so the HUD arc only ever shows for a real attacker.
  lastHit: { amount: number; sourceX: number | null; sourceZ: number | null } | null = null;

  private readonly body: THREE.Group;
  private readonly head: THREE.Mesh;
  private readonly rightArm: THREE.Group;
  private readonly leftArm: THREE.Group;
  private readonly leftLeg: THREE.Group;
  private readonly rightLeg: THREE.Group;
  private readonly handSocket: THREE.Group; // 3rd-person held item, in the right hand
  private heldItem: HeldItemId = 'rock';
  private aimPoseT = 0; // eased 0..1 toward combatAim: blends the held model between its REST and AIM view fits
  private jumpCd = 0;        // anti-bunny-hop: time left before another jump is allowed
  // Big warm light; low decay spreads it evenly (less hotspot ON the player, same world reach). ALWAYS
  // visible (intensity 0 when stowed): toggling `visible` changed the scene's point-light count, which
  // forced three.js to recompile every lit material's shader program on the first torch equip (a big
  // hitch). Kept visible, the boot compile bakes the light in and equip only ramps a uniform.
  readonly torchLight = new THREE.PointLight(0xffb066, 0, TORCH_FX.distance, 1.0);
  private torchT = 0;        // torch flicker clock
  private torchIgnite = 1;   // 0..1 flame catch-on ramp, reset on each torch equip
  private torchFlame3p: THREE.Object3D | null = null; // 3rd-person flame cone (scaled during ignition/flicker)
  private torchGlow3p: THREE.Object3D | null = null;  // 3rd-person glow sprite (same)
  private swingTimer = 0;
  private swingDur = SWING_DURATION; // current swing's full animation length (melee weapons run longer)
  private swingYaw: number | null = null;
  private gunKick = 0;       // 3rd-person gun recoil hitch (impulsed by gunRecoil(), decays)
  private gunFireT = 0;      // brief "raise + point the gun forward" window after a shot (so hip-fire doesn't kick the arm behind)
  private aimYaw: number | null = null; // briefly face the shot direction after firing (3rd person)
  private aimTimer = 0;
  // Continuous 3P aim tracking (aim overhaul #5): main feeds the resolved aim EVERY frame a weapon is
  // wielded, so the torso + raised arm point at the cursor's target between shots (the barrel is honest).
  // Consumed-per-frame: update() reads it, then clears it -- no stale facing when the weapon is stowed.
  private aimTrackYaw: number | null = null;
  private aimTrackPitch = 0;
  trackAim(yaw: number, pitch: number): void {
    this.aimTrackYaw = yaw;
    this.aimTrackPitch = Math.max(-0.6, Math.min(0.6, pitch));
  }
  private walkPhase = 0;
  private swimPhase = 0; // swim-stroke cycle phase (drives the breaststroke arms + flutter legs while swimming)
  private footfalls = 0; // increments once per footfall (drives footstep audio in sync with the legs)
  private crouchFactor = 0;
  private leanAmount = 0;
  private readonly leanOffset = new THREE.Vector3();
  private readonly tmpHoriz = new THREE.Vector3();
  private readonly tmpTarget = new THREE.Vector3();

  constructor() {
    const built = buildHumanoid();
    this.body = built.body;
    this.head = built.head;
    this.rightArm = built.rightArm;
    this.leftArm = built.leftArm;
    this.leftLeg = built.leftLeg;
    this.rightLeg = built.rightLeg;
    this.handSocket = built.handSocket;
    this.setHeldItem('rock');
    this.object.add(this.body);
    // Torch light hangs off the root object (NOT the body), so it still lights the world in first person
    // where the body is hidden. Sits at right-hand height, slightly forward.
    this.torchLight.position.set(0.3, 1.55, 0.5); // up + forward of the torso so the body sits in the falloff, not the hotspot
    this.object.add(this.torchLight);
    this.body.traverse((o) => { if ((o as THREE.Mesh).isMesh) o.castShadow = true; });
    this.object.position.copy(this.position);
  }

  // First-person: hide the head and the 3rd-person arms (a dedicated viewmodel draws the arms +
  // tool out front); keep the torso and legs so looking down you still see your body. Others see all.
  setFirstPerson(v: boolean): void {
    this.body.visible = !v; // clean hands-only view; the camera viewmodel draws the arms + tool
    void this.head;
  }

  // Third-person held item: swap a lightweight mesh in the right hand so other players (and the
  // 3rd-person camera) see what you hold. The torch stays in the left hand regardless.
  setHeldItem(id: HeldItemId): void {
    if (id === this.heldItem && this.handSocket.children.length > 0) return;
    this.heldItem = id;
    disposeChildren(this.handSocket);
    if (id) this.handSocket.add(buildHeldItem(id));
    // Torch equip = a fresh strike: the flame + light ramp in over TORCH_FX.igniteSecs (see update()).
    // The light itself stays VISIBLE forever (see the field comment) -- only intensity gates it.
    if (id === 'torch') { this.torchIgnite = 0; this.torchT = 0; }
    else this.torchLight.intensity = 0;
    this.torchFlame3p = id === 'torch' ? this.handSocket.getObjectByName('torchFlame') ?? null : null;
    this.torchGlow3p = id === 'torch' ? this.handSocket.getObjectByName('torchGlow') ?? null : null;
  }

  // Rain intensity 0..1, set per-frame by main: full rain dims the torch ~15% (owner rule).
  torchRainDim = 0;

  // Rebuild the 3P held model (bypasses the same-id guard) -- live F4 gun-fit edits re-seat instantly.
  refreshHeld(): void {
    const id = this.heldItem;
    this.heldItem = null;
    this.setHeldItem(id);
  }

  // True while the torch is wielded AND actually burning -- water DOUSES it (swimming = the hand is
  // submerged), so grass/water glow and the lit-burn durability drain all go out together underwater.
  get torchLit(): boolean { return this.heldItem === 'torch' && !this.swimming; }

  // Torch flame life: ignition ramp (smoothstepped, with a sputter that dies as it catches), then a
  // perpetual layered-sine flicker on light intensity + reach and on the 3rd-person flame mesh. All
  // sines on one clock: cheap, no allocation, no RNG.
  private tickTorch(dt: number): void {
    // DOUSED underwater: no flame, no light, and the ignition resets so surfacing re-strikes it fresh.
    if (this.swimming) {
      this.torchIgnite = 0; this.torchLight.intensity = 0;
      this.torchFlame3p?.scale.setScalar(0.02);
      this.torchGlow3p?.scale.setScalar(0.02);
      return;
    }
    this.torchT += dt;
    this.torchIgnite = Math.min(1, this.torchIgnite + dt / TORCH_FX.igniteSecs);
    const ig = this.torchIgnite * this.torchIgnite * (3 - 2 * this.torchIgnite) * torchState.lit01; // OFF by default (owner): right-click toggles
    (this.handSocket.getObjectByName('torchFx')?.userData as { tick?: (dt: number, lit: number) => void } | undefined)?.tick?.(dt, torchState.lit01);
    const F = TORCH_FX.flicker;
    const flick = F.base + F.s1 * Math.sin(this.torchT * F.f1) + F.s2 * Math.sin(this.torchT * F.f2) + F.s3 * Math.sin(this.torchT * F.f3);
    const sputter = (1 - ig) * TORCH_FX.sputter * Math.sin(this.torchT * 43);
    const rainHinder = 1 - 0.15 * this.torchRainDim; // rain beats the flame down ~15% at a full storm
    this.torchLight.intensity = TORCH_FX.intensity * ig * Math.max(0, flick + sputter) * rainHinder;
    // Reach grows with ignition + wobbles a touch (feeds the grass/water torch radius in main).
    this.torchLight.distance = TORCH_FX.distance * (0.6 + 0.4 * ig) * (1 + TORCH_FX.distFlicker * Math.sin(this.torchT * 7.1));
    const fs = Math.max(0.02, ig * (1 + TORCH_FX.flameFlicker * Math.sin(this.torchT * 21)));
    this.torchFlame3p?.scale.setScalar(fs);
    this.torchGlow3p?.scale.setScalar(0.9 * fs);
  }

  // Swing progress for the first-person viewmodel: 0..1 through a swing, < 0 when idle.
  get swingPhase(): number {
    return this.swingTimer > 0 ? 1 - this.swingTimer / this.swingDur : -1;
  }

  focusPoint(out: THREE.Vector3): THREE.Vector3 {
    return out.set(
      this.position.x + this.leanOffset.x,
      this.position.y + currentHeight(this.crouchFactor) * 0.5,
      this.position.z + this.leanOffset.z,
    );
  }

  get speed(): number {
    return Math.hypot(this.velocity.x, this.velocity.z);
  }

  // Monotonic footfall counter (one tick each time a foot plants in the walk cycle). Footstep audio fires
  // on each increment so the sound matches the visible feet at any speed.
  get footfallCount(): number { return this.footfalls; }

  // Raw walk-cycle phase (radians): sin(walkPhase) is exactly the leg-swing signal above, so the
  // first-person head-bob (camera.ts) can lock its bob to the same phase the visible feet/footstep
  // audio use, instead of running its own independent clock.
  get walkCyclePhase(): number { return this.walkPhase; }

  // Trigger an arm swing (gathering / hitting). Pass a yaw to turn and face the target; dur sizes the
  // whole animation (melee weapons pass MELEE_SWING strike+recover for the weighted heavy swing).
  swing(targetYaw?: number, dur: number = SWING_DURATION): void {
    this.swingTimer = dur;
    this.swingDur = dur;
    if (targetYaw !== undefined) this.swingYaw = targetYaw;
  }

  // A 3rd-person gun-recoil hitch: the shooting arm jerks back briefly (visible to others + when zoomed out).
  // Also opens a short "fire pose" window so a hip-fired shot momentarily raises the gun FORWARD (rather than
  // the recoil kick rotating a low-resting arm backward through the body).
  gunRecoil(): void { this.gunKick = 1; this.gunFireT = 0.22; }

  // Turn the body to face a shot direction briefly (so in 3rd person you pivot toward what you fire at).
  faceAim(yaw: number): void {
    this.aimYaw = yaw;
    this.aimTimer = 0.5;
  }

  get dead(): boolean { return this.state === 'dead'; }
  get incapacitated(): boolean { return this.state !== 'alive'; }

  // sourceX/Z: world position the damage came from (attacker/explosion), when known -- drives the HUD's
  // directional hit indicator. Omit for non-directional damage (fall, starvation, dehydration, poison).
  takeDamage(amount: number, sourceX: number | null = null, sourceZ: number | null = null): void {
    if (amount <= 0 || this.state === 'dead' || this.godMode) return; // god mode: no damage at all
    this.lastHit = { amount, sourceX, sourceZ }; // HUD directional hit indicator reads this
    this.onHurt?.();                             // hurt-grunt SFX hook
    if (this.state === 'downed') { this.die(); return; } // hit again while bleeding out -> finished
    const before = this.health;
    this.health = Math.max(0, before - amount);
    if (this.health <= 0) {
      // Revive odds: best when the blow barely killed you, worst on heavy overkill.
      const overkill = Math.min(1, Math.max(0, amount - before) / PLAYER.maxHealth);
      this.reviveChance = PLAYER.reviveChanceMax - (PLAYER.reviveChanceMax - PLAYER.reviveChanceMin) * overkill;
      this.state = 'downed';
      this.downedTimer = PLAYER.bleedOutSeconds;
      this.velocity.set(0, 0, 0);
    }
  }

  heal(amount: number): void {
    if (this.state === 'dead') return;
    this.health = Math.min(PLAYER.maxHealth, this.health + Math.max(0, amount));
  }

  // Consume a food/drink: buffer HP to slow-heal, top up hunger/thirst, and maybe poison (raw meat).
  // Returns the gains actually applied (for the contextual "+X" readout). No effect while dead.
  eat(def: { hp?: number; hunger?: number; thirst?: number; poisonSec?: number }): { hp: number; hunger: number; thirst: number } {
    if (this.state === 'dead') return { hp: 0, hunger: 0, thirst: 0 };
    const dh = Math.max(0, def.hp ?? 0);
    this.pendingHeal += dh;
    const bH = this.hunger, bT = this.thirst;
    this.hunger = Math.min(SURVIVAL.maxHunger, this.hunger + (def.hunger ?? 0));
    this.thirst = Math.min(SURVIVAL.maxThirst, this.thirst + (def.thirst ?? 0));
    if (def.poisonSec) this.poisonTimer = Math.max(this.poisonTimer, def.poisonSec);
    return { hp: dh, hunger: this.hunger - bH, thirst: this.thirst - bT };
  }

  // Drink from a river/lake: restores thirst. Returns the thirst actually gained (for the readout).
  drink(amount = SURVIVAL.drinkThirst): number {
    if (this.state === 'dead') return 0;
    const before = this.thirst;
    this.thirst = Math.min(SURVIVAL.maxThirst, this.thirst + amount);
    return this.thirst - before;
  }

  applyBleed(): void { if (!this.godMode && this.state !== 'dead') this.bleeding = true; }

  // A bandage stops bleeding and buffers a little HP to heal back.
  bandage(): void { this.bleeding = false; this.pendingHeal += SURVIVAL.bandageHeal; }

  // Advance the survival meters once per frame (alive only). Drains hunger/thirst, applies
  // starvation/dehydration + poison/bleed HP loss, and bleeds the pending-heal buffer into HP while fed.
  tickSurvival(dt: number): void {
    if (this.state !== 'alive') return;
    const burn = this.sprinting ? SURVIVAL.sprintDrainMult : 1; // running works up an appetite (survival rebalance)
    this.hunger = Math.max(0, this.hunger - SURVIVAL.hungerDrainPerSec * burn * dt);
    this.thirst = Math.max(0, this.thirst - SURVIVAL.thirstDrainPerSec * burn * dt);
    let dot = 0;
    if (this.hunger <= 0) dot += SURVIVAL.starveHpPerSec;
    if (this.thirst <= 0) dot += SURVIVAL.dehydrateHpPerSec;
    if (this.poisonTimer > 0) { this.poisonTimer = Math.max(0, this.poisonTimer - dt); dot += SURVIVAL.poisonHpPerSec; }
    if (this.bleeding) dot += SURVIVAL.bleedHpPerSec;
    if (dot > 0) this.drainHp(dot * dt);
    if (this.state === 'alive' && this.pendingHeal > 0 && this.hunger > SURVIVAL.regenHungerMin && this.thirst > SURVIVAL.regenThirstMin) {
      const h = Math.min(this.pendingHeal, SURVIVAL.healPerSec * dt, PLAYER.maxHealth - this.health);
      if (h > 0) { this.health += h; this.pendingHeal -= h; }
      if (this.health >= PLAYER.maxHealth) this.pendingHeal = 0;
    }
  }

  // Gradual HP loss (starvation / poison / bleed). Drops to DOWNED at 0 like a killing blow; the bleed-out
  // timer then decides revive-or-die, so a continuous drain does not instakill.
  private drainHp(amount: number): void {
    if (this.godMode || this.state !== 'alive' || amount <= 0) return;
    this.health = Math.max(0, this.health - amount);
    if (this.health <= 0) {
      this.reviveChance = PLAYER.reviveChanceMin; // worst odds: you let yourself bleed/starve out
      this.state = 'downed';
      this.downedTimer = PLAYER.bleedOutSeconds;
      this.velocity.set(0, 0, 0);
    }
  }

  private die(): void {
    this.state = 'dead';
    this.deathT = 0;
    this.velocity.set(0, 0, 0);
  }

  private revive(): void {
    this.state = 'alive';
    // You stand up at exactly your recovery-chance as HP (a 22% roll that hits = 22 HP).
    this.health = Math.max(1, Math.round(this.reviveChance * 100));
    this.airborneApexY = this.position.y; // same stale-apex guard as respawn: stand up, don't re-take the fall
    this.resetPose();
  }

  // Full respawn (the death-screen RESPAWN button passes a fresh point; falls back to the spawn point).
  respawn(point?: THREE.Vector3): void {
    this.position.copy(point ?? this.spawnPoint);
    this.velocity.set(0, 0, 0);
    this.health = PLAYER.maxHealth;
    this.hunger = SURVIVAL.maxHunger;
    this.thirst = SURVIVAL.maxThirst;
    this.pendingHeal = 0;
    this.poisonTimer = 0;
    this.bleeding = false;
    this.state = 'alive';
    this.grounded = false;
    // Reset the fall-arc apex to HERE. Without this, dying at height left the old apex behind, and the
    // first landing after the respawn teleport replayed the whole drop as fall damage -- you arrived at
    // the beach and were instantly downed again by a phantom 100m+ fall.
    this.airborneApexY = this.position.y;
    this.object.visible = true; // show the body again (it was hidden on death so only the corpse remained)
    this.resetPose();
  }

  private resetPose(): void {
    this.body.rotation.set(0, 0, 0);
    this.body.scale.y = 1;
    this.head.rotation.set(0, 0, 0);
    this.rightArm.rotation.set(0, 0, 0);
    this.leftArm.rotation.set(0, 0, 0);
    this.leftLeg.rotation.set(0, 0, 0);
    this.rightLeg.rotation.set(0, 0, 0);
  }

  // Downed/dead: no control, just settle under gravity, tick the timer, and pose the body.
  private updateIncapacitated(dt: number, collide?: CollideFn): void {
    this.velocity.x = 0;
    this.velocity.z = 0;
    if (!this.grounded) this.velocity.y -= MOVE.gravity * dt;
    this.moveAndCollide(dt, PLAYER.crouchHeight, collide);
    if (this.state === 'downed') {
      this.downedTimer = Math.max(0, this.downedTimer - dt);
      if (this.downedTimer <= 0) {
        if (Math.random() < this.reviveChance) { this.revive(); this.object.position.copy(this.position); return; }
        this.die();
      }
    }
    if (this.state === 'downed') this.applyDownedPose();
    else { this.deathT = Math.min(PLAYER.deathAnimSeconds, this.deathT + dt); this.applyDeathPose(this.deathT / PLAYER.deathAnimSeconds); }
    this.object.position.copy(this.position);
  }

  // Lie on the back (tipped at the feet), head propped up, one hand reaching skyward.
  private applyDownedPose(): void {
    this.body.scale.y = 1;
    this.body.rotation.set(-1.45, 0, 0); // ~83deg: nearly flat, head end just off the ground
    this.head.rotation.set(0.9, 0, 0);    // prop the head up
    this.rightArm.rotation.set(-2.4, 0, 0); // hand up
    this.leftArm.rotation.set(0.2, 0, 0);
    this.leftLeg.rotation.x = 0.1;
    this.rightLeg.rotation.x = -0.1;
  }

  // Collapse/flop to the side over t=0..1, then hold (a canned ragdoll, not physics).
  private applyDeathPose(t: number): void {
    const e = t * t * (3 - 2 * t); // smoothstep
    this.body.scale.y = 1;
    this.body.rotation.set(-1.2 * e, 0, 1.5 * e); // sink + flop sideways
    this.head.rotation.set(0.3 * e, 0, 0.5 * e);
    this.rightArm.rotation.set(-0.4 * e, 0, 0);
    this.leftArm.rotation.set(0.4 * e, 0, 0);
    this.leftLeg.rotation.x = 0.3 * e;
    this.rightLeg.rotation.x = -0.2 * e;
  }

  update(dt: number, input: InputState, moveYaw: number, collide?: CollideFn, headroom?: HeadroomFn): void {
    if (this.heldItem === 'torch') this.tickTorch(dt);
    if (this.state !== 'alive') { this.updateIncapacitated(dt, collide); return; } // downed/dead: no control
    // In water (not flying): the body is below the surface deep enough to swim rather than wade.
    const submerged = !this.fly && this.position.y < TERRAIN.seaLevel - 0.3;
    this.swimming = submerged;

    // Crouch: held, OR FORCED only under a genuine low CEILING you can crouch beneath (a floor above you
    // between storeys). A world PROP -- a barrel, crate or rock -- sitting on slightly higher ground has its
    // collider bottom above the feet but BELOW crouch height; that is a solid object you collide with, NOT a
    // ceiling, so it must never squash or auto-crouch you (owner: never auto-crouch from world objects).
    // Treat a box as a ceiling only when there is at least crouch-height of clearance under it.
    const room = !this.fly && !submerged && headroom ? headroom(this.position.x, this.position.z, this.position.y, PLAYER.radius, PLAYER.height) : PLAYER.height;
    const ceiling = room >= PLAYER.crouchHeight ? room : PLAYER.height; // ignore sub-crouch props entirely
    const forcedCrouch = ceiling < PLAYER.height - 0.05;
    this.crouching = !submerged && (input.crouch || forcedCrouch);
    const crouchTarget = this.crouching ? 1 : 0;
    this.crouchFactor += (crouchTarget - this.crouchFactor) * Math.min(1, dt * 14);
    let height = currentHeight(this.crouchFactor);
    if (height > ceiling) height = Math.max(PLAYER.crouchHeight, ceiling); // hard clamp: never exceed a real ceiling

    // Camera-relative intent.
    const ix = (input.right ? 1 : 0) - (input.left ? 1 : 0);
    const iz = (input.forward ? 1 : 0) - (input.back ? 1 : 0);
    const sin = Math.sin(moveYaw);
    const cos = Math.cos(moveYaw);
    this.tmpHoriz.set(cos * ix - sin * iz, 0, -sin * ix - cos * iz);
    const hasInput = this.tmpHoriz.lengthSq() > 0;
    if (hasInput) this.tmpHoriz.normalize();

    if (this.fly) {
      // Dev fly: free 3D movement, no gravity or collision. Jump = up, crouch = down, sprint = fast.
      const sp = MOVE.flySpeed * (input.sprint ? 2.4 : 1);
      const up = (input.jump ? 1 : 0) - (input.crouch ? 1 : 0);
      this.velocity.set(this.tmpHoriz.x * sp, up * sp, this.tmpHoriz.z * sp);
      this.position.addScaledVector(this.velocity, dt);
      this.grounded = false;
      this.sprinting = false;
      this.stamina = Math.min(MOVE.staminaMax, this.stamina + MOVE.staminaRegenPerSec * dt);
    } else if (submerged) {
      // SWIM: buoyancy floats you to the surface; JUMP rises, CROUCH dives; slower horizontal + drag.
      this.sprinting = false;
      const sp = WATER.swimSpeed * (input.sprint ? 1.4 : 1);
      this.tmpTarget.set(this.tmpHoriz.x * sp, 0, this.tmpHoriz.z * sp);
      const horiz = new THREE.Vector3(this.velocity.x, 0, this.velocity.z);
      moveToward(horiz, this.tmpTarget, MOVE.accel * dt);
      this.velocity.x = horiz.x;
      this.velocity.z = horiz.z;
      let vy = this.velocity.y;
      if (input.jump) vy += WATER.swimVertical * 6 * dt;        // kick up
      else if (input.crouch) vy -= WATER.swimVertical * 6 * dt; // dive
      else vy += (TERRAIN.seaLevel - WATER.surfaceBob - this.position.y) * WATER.buoyancy * dt; // bob to surface
      vy -= vy * Math.min(1, WATER.drag * dt);                  // water drag
      this.velocity.y = Math.max(-WATER.swimVertical, Math.min(WATER.swimVertical, vy));
      this.stamina = Math.min(MOVE.staminaMax, this.stamina + MOVE.staminaRegenPerSec * dt);
      this.moveAndCollide(dt, height, collide);
    } else {
      const canSprint = input.sprint && hasInput && !this.crouching && !this.aiming && this.stamina > MOVE.staminaSprintFloor;
      this.sprinting = canSprint;
      // WADING: feet below the waterline but not deep enough to swim -- shallow water drags you to a slow slog.
      const wading = this.position.y < TERRAIN.seaLevel;
      const maxSpeed = (this.crouching ? MOVE.crouchSpeed : this.sprinting ? MOVE.sprintSpeed : MOVE.walkSpeed) * (wading ? MOVE.waterSpeedFactor : 1);

      // Slope difficulty: project the move direction onto the terrain gradient to get the UPHILL grade
      // (rise/run). Going straight up a steep face slows hard and stalls past slopeStall; flat and downhill
      // stay full speed, and traversing a slope (moving across it) keeps the grade low so you can still wind
      // a gentler route up. Sampled only while actually moving on foot.
      let slopeFactor = 1;
      if (hasInput) {
        const e = MOVE.slopeSampleStep;
        const px = this.position.x, pz = this.position.z;
        const gx = (terrainHeight(px + e, pz) - terrainHeight(px - e, pz)) / (2 * e);
        const gz = (terrainHeight(px, pz + e) - terrainHeight(px, pz - e)) / (2 * e);
        const grade = this.tmpHoriz.x * gx + this.tmpHoriz.z * gz; // >0 ascending, <=0 flat/downhill
        if (grade > MOVE.slopeEasy) {
          const t = Math.min(1, (grade - MOVE.slopeEasy) / (MOVE.slopeStall - MOVE.slopeEasy));
          slopeFactor = 1 + (MOVE.slopeMinFactor - 1) * (t * t * (3 - 2 * t)); // smoothstep 1 -> slopeMinFactor
        }
      }
      const effSpeed = maxSpeed * slopeFactor * (this.aiming ? ADS_MOVE_SLOW : 1); // aiming down sights slows you to a steady walk

      // Horizontal velocity with momentum.
      this.tmpTarget.set(this.tmpHoriz.x * effSpeed, 0, this.tmpHoriz.z * effSpeed);
      const horiz = new THREE.Vector3(this.velocity.x, 0, this.velocity.z);
      moveToward(horiz, this.tmpTarget, (hasInput ? MOVE.accel : MOVE.decel) * dt);
      this.velocity.x = horiz.x;
      this.velocity.z = horiz.z;

      if (this.sprinting) this.stamina = Math.max(0, this.stamina - MOVE.staminaDrainPerSec * dt);
      else this.stamina = Math.min(MOVE.staminaMax, this.stamina + MOVE.staminaRegenPerSec * dt);

      // Jump and gravity. Crouch-jump (jump while crouched) reaches higher. A cooldown blocks an instant
      // re-jump on landing, so you cannot bunny-hop.
      if (this.jumpCd > 0) this.jumpCd = Math.max(0, this.jumpCd - dt);
      if (input.jump && this.grounded && this.jumpCd <= 0) {
        this.velocity.y = this.crouching ? MOVE.crouchJumpVelocity : MOVE.jumpVelocity;
        this.grounded = false;
        this.jumpCd = MOVE.jumpCooldown;
      }
      // Gravity ALWAYS applies (even when grounded): standing still, it sinks you a hair into the floor each
      // frame so the collision re-confirms ground contact and keeps `grounded` stably true. Skipping it while
      // grounded made grounded flip false->true every frame (no re-sink to detect), which spammed footsteps +
      // jump/land. The collision clamps you back to the surface + zeroes vy, so there is no visible sink.
      this.velocity.y -= MOVE.gravity * dt;
      // Fall damage from the ACTUAL drop (apex while airborne -> landing height), not instantaneous vy.
      // Climbing or jumping straight UP a steep mountain snaps the player up the slope, which spiked vy
      // and read as a lethal "impact" even though you never fell. Tracking the real descent only hurts
      // you when you genuinely drop a long way.
      const wasAirborne = !this.grounded;
      if (wasAirborne) this.airborneApexY = Math.max(this.airborneApexY, this.position.y);
      else this.airborneApexY = this.position.y; // grounded: the apex reference is the ground you stand on
      this.moveAndCollide(dt, height, collide);
      if (wasAirborne && this.grounded) {
        const fallDist = this.airborneApexY - this.position.y; // how far you actually dropped this arc
        if (fallDist > 0) {
          const impactSpeed = Math.sqrt(2 * MOVE.gravity * fallDist); // v = sqrt(2 g h): keeps the old tuning
          if (impactSpeed > MOVE.fallSafe) this.takeDamage((impactSpeed - MOVE.fallSafe) * MOVE.fallDamagePerSpeed);
        }
      }
    }

    // Crouch-peek lean (camera-relative, feet stay).
    const leanTarget = (input.leanRight ? 1 : 0) - (input.leanLeft ? 1 : 0);
    this.leanAmount += (leanTarget - this.leanAmount) * Math.min(1, dt * PEEK.leanSpeed);
    this.leanOffset.set(cos * this.leanAmount * PEEK.distance, 0, -sin * this.leanAmount * PEEK.distance);

    this.body.scale.y = height / PLAYER.height;
    // Face the swing target, else the shot direction (briefly after firing), else travel -- EASED so the body
    // never pops/snaps when you swing or turn.
    // FACING CONVENTION: the body's FRONT is its -Z face -- the held item leads -Z and the hand socket is the
    // +X arm, which is the right hand only for a -Z-facing body. atan2(x,z) yaws the +Z face toward a target, so
    // we add PI to face it with -Z instead. Without this the body led with +Z, putting every held item on the
    // BACK-LEFT (the "gun in the left hand / facing backward" bug). Idle holds the current yaw (no +PI).
    let yawTarget = this.object.rotation.y;
    if (this.swingTimer > 0 && this.swingYaw !== null) yawTarget = this.swingYaw + Math.PI;
    else if (this.aimTrackYaw !== null) yawTarget = this.aimTrackYaw + Math.PI; // continuous weapon-out tracking (aim overhaul #5)
    else if (this.aimTimer > 0 && this.aimYaw !== null) yawTarget = this.aimYaw + Math.PI;
    else if (this.speed > 0.2) yawTarget = Math.atan2(this.velocity.x, this.velocity.z) + Math.PI;
    if (this.aimTimer > 0) this.aimTimer = Math.max(0, this.aimTimer - dt);
    let dyaw = ((yawTarget - this.object.rotation.y + Math.PI) % (Math.PI * 2) + Math.PI * 2) % (Math.PI * 2) - Math.PI;
    this.object.rotation.y += dyaw * Math.min(1, dt * 14);
    this.object.position.copy(this.position).add(this.leanOffset);

    // SWIM POSE: a clean HORIZONTAL front-crawl. The body lies flat (prone), the arms alternate a smooth crawl,
    // the legs flutter, and the head lifts to look ahead so it reads as swimming forward -- not a wonky splay or
    // a floating corpse. Kept tidy (moderate reach, no wide breaststroke fling).
    if (this.swimming) {
      this.object.rotation.x += (-1.45 - this.object.rotation.x) * Math.min(1, dt * 7); // ease flat to prone
      this.swimPhase += (Math.min(1, this.speed / 3) * 1.6 + 1.1) * dt * 2.6;            // stroke rate scales with speed
      const s = Math.sin(this.swimPhase);
      this.leftArm.rotation.x = 1.35 + s * 1.05;   // alternating overhead crawl (opposite phases)
      this.rightArm.rotation.x = 1.35 - s * 1.05;
      this.leftLeg.rotation.x = s * 0.32;          // gentle flutter kick
      this.rightLeg.rotation.x = -s * 0.32;
      this.head.rotation.x = 0.7;                  // head lifted to look ahead while prone
      return;
    }
    if (this.object.rotation.x !== 0) this.object.rotation.x += (0 - this.object.rotation.x) * Math.min(1, dt * 8); // ease back upright on exit

    // Walk cycle: legs and arms swing, scaled by speed (idle stays still). A foot plants at each extreme of
    // sin(walkPhase), so this counter ticks once per footfall -- footstep audio reads it so the sound is
    // locked to the visible feet (and auto-scales with speed) instead of a separate timer.
    this.walkPhase += this.speed * dt * 2.4;
    this.footfalls = Math.floor((this.walkPhase + Math.PI / 2) / Math.PI);
    const stride = Math.min(1, this.speed / 3) * 0.7;
    const swingLeg = Math.sin(this.walkPhase) * stride;
    this.leftLeg.rotation.x = swingLeg;
    this.rightLeg.rotation.x = -swingLeg;
    const armSwing = swingLeg * 0.5; // arms counter-swing OPPOSITE the same-side leg (natural gait, not lockstep)
    const aimElev = this.aimTrackYaw !== null ? this.aimTrackPitch : 0; // tracked target elevation (aim overhaul #5)
    // Left arm: supports the gun in a combat aim pose (pitching with the aim), else the natural counter-swing.
    this.leftArm.rotation.x += ((this.combatAim ? AIM_ARM - 0.15 + aimElev : -0.1 - armSwing) - this.leftArm.rotation.x) * Math.min(1, dt * 12);

    // Right-arm swing: raise up in front (windup), then slam down through the front, then rest.
    if (this.swingTimer > 0) {
      this.swingTimer = Math.max(0, this.swingTimer - dt);
      const phase = 1 - this.swingTimer / this.swingDur; // 0..1
      const windup = 0.55;
      if (phase < windup) {
        const t = phase / windup;
        this.rightArm.rotation.x = SWING_RAISE * (1 - (1 - t) * (1 - t)); // cock forward-up (never past vertical/behind)
      } else {
        const t = (phase - windup) / (1 - windup);
        this.rightArm.rotation.x = SWING_RAISE - (SWING_RAISE - SWING_END) * (t * t); // slam down through the FRONT
      }
    } else {
      this.swingYaw = null;
      // Ease the right arm to the combat aim pose (gun raised) when aiming OR briefly after a shot (so a
      // hip-fired gun snaps FORWARD to fire), else the natural walk swing. The fire window eases fast so
      // the raise reads as "point + shoot".
      const firing = this.gunFireT > 0;
      // In the aim pose the raised arm PITCHES with the tracked target elevation (aim overhaul #5), so
      // the rendered barrel points up/down at what the cursor is actually on.
      const restTarget = (this.combatAim || firing) ? AIM_ARM + aimElev : armSwing;
      this.rightArm.rotation.x += (restTarget - this.rightArm.rotation.x) * Math.min(1, dt * (firing ? 34 : 12));
    }
    if (this.gunFireT > 0) this.gunFireT = Math.max(0, this.gunFireT - dt);
    // 3rd-person gun recoil: a quick hitch back on the arm + a head snap-back, decaying over ~0.16s (returns to 0).
    // The kick is applied AFTER the arm is raised (forward), so it reads as a recoil jerk, never a swing behind.
    if (this.gunKick > 0) this.gunKick = Math.max(0, this.gunKick - dt * 6);
    this.rightArm.rotation.x -= this.gunKick * 0.3;
    this.head.rotation.x = -this.gunKick * 0.3;
    // 3P PER-VIEW FIT (owner 2026-07-12, "works with ANY item in my hand"): rest (p3/p3r) and aim
    // (p3Aim/p3rAim) are two ABSOLUTE, independently-saved configs. The held model's transform is
    // recomputed every frame from the builder's recorded default seat (userData.seat3p) + the fit for
    // the current pose, blending rest<->aim at the same rate the arm raises -- so tuning one view can
    // never disturb the other, and an untouched aim view simply mirrors the rest tune.
    this.aimPoseT += ((this.combatAim ? 1 : 0) - this.aimPoseT) * Math.min(1, dt * 12);
    {
      const child = this.handSocket.children[0];
      const seat = child?.userData.seat3p as V3 | undefined;
      if (child && seat && this.heldItem) {
        const fit = weaponFit(this.heldItem);
        if (fit.p3 || fit.p3r || fit.p3Aim || fit.p3rAim || fit.p3rot) {
          const rp = fit.p3 ?? ZERO3, rr = fit.p3r ?? ZERO3;
          const ap = fit.p3Aim ?? rp, ar = fit.p3rAim ?? rr; // aim falls back to the rest tune until touched
          const t = this.aimPoseT, u = 1 - t;
          child.position.set(seat[0] + rp[0] * u + ap[0] * t, seat[1] + rp[1] * u + ap[1] * t, seat[2] + rp[2] * u + ap[2] * t);
          child.rotation.set(rr[0] * u + ar[0] * t, (fit.p3rot ?? 0) + rr[1] * u + ar[1] * t, rr[2] * u + ar[2] * t);
        }
      }
    }
    this.aimTrackYaw = null; // consumed: main re-feeds every frame the weapon is out (no stale facing)
  }

  // Integrate the velocity and resolve collision in SUBSTEPS (<= ~0.15u each) so a fast body can never
  // tunnel through a thin wall. Falls back to the bare terrain floor when no collider set is supplied.
  private moveAndCollide(dt: number, height: number, collide?: CollideFn): void {
    if (collide) {
      const travel = Math.hypot(this.velocity.x, this.velocity.y, this.velocity.z) * dt;
      const steps = Math.min(8, Math.max(1, Math.ceil(travel / 0.15)));
      const sdt = dt / steps;
      let grounded = false;
      for (let i = 0; i < steps; i++) {
        this.position.addScaledVector(this.velocity, sdt);
        if (collide(this.position, this.velocity, PLAYER.radius, height)) grounded = true;
      }
      this.grounded = grounded;
    } else {
      this.position.addScaledVector(this.velocity, dt);
      const floor = terrainHeight(this.position.x, this.position.z);
      if (this.position.y <= floor) {
        this.position.y = floor;
        this.velocity.y = 0;
        this.grounded = true;
      }
    }
  }
}

function currentHeight(crouchFactor: number): number {
  return PLAYER.height + (PLAYER.crouchHeight - PLAYER.height) * crouchFactor;
}

function moveToward(current: THREE.Vector3, target: THREE.Vector3, maxDelta: number): void {
  const dx = target.x - current.x;
  const dz = target.z - current.z;
  const dist = Math.hypot(dx, dz);
  if (dist <= maxDelta || dist === 0) {
    current.x = target.x;
    current.z = target.z;
    return;
  }
  current.x += (dx / dist) * maxDelta;
  current.z += (dz / dist) * maxDelta;
}

// Player look (shirt / pants / skin tone) customised on the main-menu PLAYER page and persisted to
// localStorage. buildHumanoid reads it so the choice shows on the 3rd-person body in-game. Defaults match
// the original hardcoded colours.
export interface PlayerSkin { skin: number; shirt: number; pants: number; }
// Exported as the NEUTRAL fallback for remote bodies whose look never arrived (old server) -- a sleeper must
// default to these, never to the VIEWER's own getPlayerSkin() (that was the "her body wears my shirt" bug).
export const PLAYER_SKIN_DEFAULT: PlayerSkin = { skin: 0xcf9b6a, shirt: 0x5c6b7a, pants: 0x3c4654 };
const PLAYER_SKIN_KEY = 'oxide.playerSkin';
export function getPlayerSkin(): PlayerSkin {
  try {
    const raw = localStorage.getItem(PLAYER_SKIN_KEY);
    if (raw) { const s = JSON.parse(raw) as Partial<PlayerSkin>; return { skin: s.skin ?? PLAYER_SKIN_DEFAULT.skin, shirt: s.shirt ?? PLAYER_SKIN_DEFAULT.shirt, pants: s.pants ?? PLAYER_SKIN_DEFAULT.pants }; }
  } catch { /* ignore */ }
  return { ...PLAYER_SKIN_DEFAULT };
}
export function savePlayerSkin(s: PlayerSkin): void { try { localStorage.setItem(PLAYER_SKIN_KEY, JSON.stringify(s)); } catch { /* ignore */ } }

// Simple low-poly humanoid: legs, torso, head, two arms. Right arm pivots at the shoulder so
// it can swing; it holds the rock. Left arm holds the torch (a warm point light, the night tell).
export function buildHumanoid(look: PlayerSkin = getPlayerSkin()): { body: THREE.Group; head: THREE.Mesh; rightArm: THREE.Group; leftArm: THREE.Group; leftLeg: THREE.Group; rightLeg: THREE.Group; handSocket: THREE.Group } {
  const g = new THREE.Group();
  // Blocky Minecraft-style humanoid: flat-shaded boxes throughout (owner prefers this over the rounded pass).
  // `look` is the LOCAL player's skin by default, but a REMOTE player passes ITS OWN skin so everyone renders
  // with their real colours (the fix for "all players look the same colour" in multiplayer).
  const skin = new THREE.MeshLambertMaterial({ color: look.skin, flatShading: true });
  const shirt = new THREE.MeshLambertMaterial({ color: look.shirt, flatShading: true });
  const pants = new THREE.MeshLambertMaterial({ color: look.pants, flatShading: true });

  // Legs pivot at the hip so they swing for the walk cycle.
  const legGeo = new THREE.BoxGeometry(0.22, 0.85, 0.25);
  const leftLeg = new THREE.Group();
  leftLeg.position.set(-0.13, 0.85, 0);
  const leftLegMesh = new THREE.Mesh(legGeo, pants);
  leftLegMesh.position.set(0, -0.42, 0);
  leftLeg.add(leftLegMesh);
  const rightLeg = new THREE.Group();
  rightLeg.position.set(0.13, 0.85, 0);
  const rightLegMesh = new THREE.Mesh(legGeo, pants);
  rightLegMesh.position.set(0, -0.42, 0);
  rightLeg.add(rightLegMesh);

  const torso = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.62, 0.3), shirt);
  torso.position.set(0, 1.16, 0);

  const head = new THREE.Mesh(new THREE.BoxGeometry(0.28, 0.3, 0.28), skin);
  head.position.set(0, 1.62, 0);
  // A simple face on the FRONT (-Z, the direction the body leads with). Makes the heading readable at a
  // glance -- so which way the player faces, and which hand holds the tool, is never ambiguous again.
  const eyeWhite = new THREE.MeshLambertMaterial({ color: 0xf2efe6, flatShading: true });
  const eyePupil = new THREE.MeshLambertMaterial({ color: 0x24201c, flatShading: true });
  for (const sx of [-1, 1]) {
    const w = new THREE.Mesh(new THREE.BoxGeometry(0.075, 0.07, 0.02), eyeWhite);
    w.position.set(sx * 0.066, 1.655, -0.141); // -Z face, slightly proud
    const p = new THREE.Mesh(new THREE.BoxGeometry(0.035, 0.055, 0.02), eyePupil);
    p.position.set(sx * 0.066, 1.655, -0.148);
    g.add(w, p);
  }

  const armGeo = new THREE.BoxGeometry(0.15, 0.55, 0.17);

  // Left arm. (The torch is no longer a permanent fixture: it is a wielded item now, see buildHeldItem.)
  const leftArm = new THREE.Group();
  leftArm.position.set(-0.33, 1.42, 0);
  const leftLimb = new THREE.Mesh(armGeo, skin);
  leftLimb.position.set(0, -0.27, 0);
  leftArm.add(leftLimb);
  leftArm.rotation.x = -0.1; // a natural rest (was -0.5: a permanent forward reach left over from the torch days)

  // Right arm (swings) holds the rock.
  const rightArm = new THREE.Group();
  rightArm.position.set(0.33, 1.42, 0);
  const rightLimb = new THREE.Mesh(armGeo, skin);
  rightLimb.position.set(0, -0.27, 0);
  rightArm.add(rightLimb);
  // Held item lives in this socket at the hand (bottom of the forearm). setHeldItem fills it.
  const handSocket = new THREE.Group();
  handSocket.position.set(0, -0.55, 0);
  rightArm.add(handSocket);

  g.add(leftLeg, rightLeg, torso, head, leftArm, rightArm);
  return { body: g, head, rightArm, leftArm, leftLeg, rightLeg, handSocket };
}

// Torch MESH only (no light). The world-lighting torch light lives on the player object so it survives
// first person + does not multiply across remote players (see Player.torchLight).
function buildTorch(): THREE.Group {
  const glb = buildTorchGlb(); // owner on/off GLB + fire/smoke rig; procedural fallback below until warm
  if (glb) return glb;
  const t = new THREE.Group();
  const stick = new THREE.Mesh(new THREE.CylinderGeometry(0.04, 0.05, 0.7, 5), new THREE.MeshLambertMaterial({ color: 0x5a3b22 }));
  stick.position.y = 0.35;
  // Flame geometry is anchored at its BASE (not center) so the ignition scale-up grows out of the
  // stick tip instead of floating. Named so Player.tickTorch can find + animate it after a swap.
  const flame = new THREE.Mesh(new THREE.ConeGeometry(0.13, 0.34, 7).translate(0, 0.17, 0), new THREE.MeshBasicMaterial({ color: 0xffae42 }));
  flame.position.y = 0.65;
  flame.name = 'torchFlame';
  // Soft additive glow so the flame reads as a light source / bloom (matches the campfire).
  const glow = new THREE.Sprite(new THREE.SpriteMaterial({ map: makeGlowTexture(), color: 0xffb060, blending: THREE.AdditiveBlending, transparent: true, depthWrite: false, opacity: 0.7 }));
  glow.position.y = 0.84;
  glow.scale.setScalar(0.9);
  glow.name = 'torchGlow';
  t.add(stick, flame, glow);
  return t;
}

// Same GLB as viewmodel.ts's FP rock (preloadGLB is dedup'd by URL, so calling it from both files is safe).
const ROCK_3P_FILE = 'rock.glb';
preloadGLB('/models/' + ROCK_3P_FILE);

// Simple 3rd-person held item for the right hand. The hand socket hangs at the bottom of the
// forearm, so models sit around the origin and read at a glance (no fine detail needed at range).
// EVERY non-weapon handheld gets the F8 3P fit (p3/p3r/scale) applied once, centrally (owner spec).
export function buildHeldItem(id: Exclude<HeldItemId, null>): THREE.Group {
  if (isWeapon(id)) return buildWeapon3p(id);
  const g = new THREE.Group();
  buildHeldItemInner(g, id);
  applyHeldFit(g, id, true);
  return g;
}
function buildHeldItemInner(g: THREE.Group, id: Exclude<HeldItemId, null>): void {
  switch (id) {
    case 'rock': {
      // Owner-modelled GLB (2026-07-10): same fallback pattern as viewmodel.ts's FP buildRock().
      const ready = cloneGLBIfReady('/models/' + ROCK_3P_FILE);
      if (ready) { g.add(fitGLBHeld(ready, ROCK_3P_FILE, 1)); break; }
      const rock = new THREE.Mesh(new THREE.IcosahedronGeometry(0.14, 0), new THREE.MeshLambertMaterial({ color: 0x8a8f96, flatShading: true }));
      g.add(rock);
      break;
    }
    case 'torch': {
      g.add(buildTorch()); // mesh only; the light is on the player object
      break;
    }
    case 'guidebook': {
      // A small closed field guide held in the hand: leather cover + pale page block.
      const cover = new THREE.Mesh(new THREE.BoxGeometry(0.17, 0.03, 0.22), new THREE.MeshLambertMaterial({ color: 0x7a4f2b, flatShading: true }));
      const pages = new THREE.Mesh(new THREE.BoxGeometry(0.155, 0.022, 0.205), new THREE.MeshLambertMaterial({ color: 0xe8ddc4, flatShading: true }));
      pages.position.y = 0.004;
      g.add(cover, pages);
      break;
    }
    case 'hammer': {
      // Owner-modelled GLB (2026-07-11) with the old procedural claw hammer as the warm-up fallback.
      g.add(buildToolGlb('hammer', () => {
        const f = new THREE.Group();
        const wood = new THREE.MeshLambertMaterial({ color: 0x6b4a2b, flatShading: true });
        const metal = new THREE.MeshLambertMaterial({ color: 0x9aa0a6, flatShading: true });
        const handle = new THREE.Mesh(new THREE.CylinderGeometry(0.026, 0.032, 0.5, 6), wood);
        handle.position.y = -0.08;
        const head = new THREE.Mesh(new THREE.BoxGeometry(0.09, 0.1, 0.18), metal);
        head.position.set(0, 0.2, -0.01);
        const face = new THREE.Mesh(new THREE.BoxGeometry(0.11, 0.12, 0.05), metal);
        face.position.set(0, 0.2, -0.11); // flat striking face, forward
        const claw = new THREE.Mesh(new THREE.BoxGeometry(0.05, 0.05, 0.09), metal);
        claw.position.set(0, 0.2, 0.12); // claw stub, back
        f.add(handle, head, face, claw);
        return f;
      }));
      break;
    }
    case 'blueprint': {
      const ready = cloneGLBIfReady('/models/blueprint.glb');
      if (ready) { const f = fitGLBHeld(ready, 'blueprint.glb', 0.4); f.rotation.x = -0.4; g.add(f); break; }
      const paper = new THREE.MeshLambertMaterial({ color: 0x2f6fb3, flatShading: true });
      const sheet = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.36, 0.03), paper);
      sheet.rotation.x = -0.4;
      g.add(sheet);
      break;
    }
    case 'chest':
    case 'bigchest': {
      const s = id === 'bigchest' ? 0.22 : 0.17;
      const wood = new THREE.MeshLambertMaterial({ color: 0x7a5230, flatShading: true });
      const lid = new THREE.MeshLambertMaterial({ color: 0x8a5f37, flatShading: true });
      const body = new THREE.Mesh(new THREE.BoxGeometry(s * 1.4, s, s), wood);
      const top = new THREE.Mesh(new THREE.BoxGeometry(s * 1.4, s * 0.5, s), lid);
      top.position.y = s * 0.72;
      g.add(body, top);
      break;
    }
    case 'wood_axe': g.add(buildToolGlb('wood_axe', () => buildToolMesh('axe', 0x8a6a3a))); break;
    case 'stone_axe': g.add(buildToolGlb('stone_axe', () => buildToolMesh('axe', 0x8f9298))); break;
    case 'metal_axe': g.add(buildToolGlb('metal_axe', () => buildToolMesh('axe', 0xc2c7cd))); break;
    case 'wood_pickaxe': g.add(buildToolGlb('wood_pickaxe', () => buildToolMesh('pickaxe', 0x7a5e34))); break;
    case 'stone_pickaxe': g.add(buildToolGlb('stone_pickaxe', () => buildToolMesh('pickaxe', 0x82868c))); break;
    case 'metal_pickaxe': g.add(buildToolGlb('metal_pickaxe', () => buildToolMesh('pickaxe', 0xb4b9c0))); break;
  }
}

// Axe / pickaxe held mesh: a wood handle with a tier-coloured head. Axe head is a forward wedge; pickaxe
// head is a crossways bar. Used by both the 3rd-person hand and (a smaller copy) the first-person viewmodel.
function buildToolMesh(kind: 'axe' | 'pickaxe', headColor: number): THREE.Group {
  const g = new THREE.Group();
  const wood = new THREE.MeshLambertMaterial({ color: 0x6b4a2b, flatShading: true });
  const head = new THREE.MeshLambertMaterial({ color: headColor, flatShading: true });
  const handle = new THREE.Mesh(new THREE.CylinderGeometry(0.028, 0.034, 0.5, 6), wood);
  handle.position.y = -0.05;
  if (kind === 'axe') {
    const blade = new THREE.Mesh(new THREE.BoxGeometry(0.05, 0.16, 0.2), head);
    blade.position.set(0, 0.2, -0.1); // -Z = forward: the bit LEADS forward (was +0.1 = facing backward)
    g.add(handle, blade);
  } else {
    // Pickaxe head runs fore-aft with the pick tip leading forward (never side to side).
    const bar = new THREE.Mesh(new THREE.BoxGeometry(0.05, 0.05, 0.42), head);
    bar.position.set(0, 0.2, -0.06);
    bar.rotation.x = 0.2; // tip dips forward like a real pick
    g.add(handle, bar);
  }
  return g;
}

// Dispose a group's children (geometry + materials) before swapping a held item out.
function disposeChildren(parent: THREE.Object3D): void {
  for (const child of [...parent.children]) {
    parent.remove(child);
    child.traverse((o) => {
      const m = o as THREE.Mesh;
      if (m.isMesh) {
        m.geometry.dispose();
        const mat = m.material;
        if (Array.isArray(mat)) mat.forEach((x) => x.dispose());
        else mat.dispose();
      }
    });
  }
}
